import React, { useEffect, useState, createContext, useContext, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { auth, googleProvider } from "./lib/firebase";
import { signInWithRedirect, getRedirectResult, signOut, onAuthStateChanged } from "firebase/auth";
import { AnimatePresence, motion } from "framer-motion";

// Pages
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import CollegeDetail from "./pages/CollegeDetail";
import SubmitPlacement from "./pages/SubmitPlacement";
import Profile from "./pages/Profile";
import Login from "./pages/Login";
import Admin from "./pages/Admin";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import AboutUs from "./pages/AboutUs";

// Components
import { ThemeProvider } from "./components/ThemeProvider";
import BottomNav from "./components/BottomNav";
import BackToTop from "./components/BackToTop";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Storage keys
const TOKEN_KEY = 'transplacement_token';
const REDIRECT_KEY = 'transplacement_redirect';

// Token helpers
const getToken = () => localStorage.getItem(TOKEN_KEY);
const setToken = (t) => t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY);
const getRedirectTarget = () => localStorage.getItem(REDIRECT_KEY);
const setRedirectTarget = (t) => t ? localStorage.setItem(REDIRECT_KEY, t) : localStorage.removeItem(REDIRECT_KEY);

// Axios interceptor - add auth token to all requests
axios.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log('[Axios] Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('[Axios] Request error:', error);
    return Promise.reject(error);
  }
);

// Axios response interceptor for debugging
axios.interceptors.response.use(
  (response) => {
    console.log('[Axios] Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('[Axios] Response error:', error.message, error.config?.url);
    if (error.response) {
      console.error('[Axios] Status:', error.response.status);
    }
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = createContext(null);
const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [pendingRedirect, setPendingRedirect] = useState(null);
  const isCreatingSession = useRef(false);
  const hasCheckedRedirect = useRef(false);
  const loginInProgress = useRef(false);
  const lastAuthUser = useRef(null); // Track last auth user to prevent duplicate processing

  // Create backend session from Firebase user
  const createSession = useCallback(async (firebaseUser) => {
    if (isCreatingSession.current) {
      console.log("[Auth] Session creation already in progress");
      return null;
    }
    
    isCreatingSession.current = true;
    setSessionLoading(true);
    console.log("[Auth] Creating session for:", firebaseUser.email);
    
    try {
      const idToken = await firebaseUser.getIdToken(true);
      const res = await axios.post(`${API}/auth/firebase-session`, { id_token: idToken });
      
      if (res.data.session_token) {
        setToken(res.data.session_token);
        console.log("[Auth] ✓ Session created, token saved");
        isCreatingSession.current = false;
        setSessionLoading(false);
        return res.data.user;
      }
    } catch (err) {
      console.error("[Auth] Session creation failed:", err.response?.data || err.message);
    }
    
    isCreatingSession.current = false;
    setSessionLoading(false);
    return null;
  }, []);

  // Verify stored token
  const verifyToken = useCallback(async () => {
    const token = getToken();
    if (!token) return null;
    
    try {
      console.log("[Auth] Verifying stored token...");
      const res = await axios.get(`${API}/auth/me`, { 
        timeout: 10000,
         
      });
      if (res.data?.user_id) {
        console.log("[Auth] ✓ Token valid for:", res.data.email);
        return res.data;
      }
    } catch (err) {
      console.log("[Auth] Token invalid, clearing");
      setToken(null);
    }
    return null;
  }, []);

  // Handle successful login - with duplicate prevention
  const handleLoginSuccess = useCallback((userData) => {
    // Prevent duplicate processing
    if (user && user.email === userData.email) {
      console.log("[Auth] Login success already processed for:", userData.email);
      setLoading(false);
      return;
    }
    
    console.log("[Auth] Login success for:", userData.email);
    setUser(userData);
    setLoading(false);
    loginInProgress.current = false; // Clear login in progress
    
    const target = getRedirectTarget();
    if (target) {
      console.log("[Auth] Redirecting to:", target);
      setRedirectTarget(null);
      localStorage.removeItem('transplacement_login_time'); // Clear login timestamp
      toast.success("Welcome!");
      // Use setTimeout to ensure state is updated before redirect
      setTimeout(() => setPendingRedirect(target), 50);
    }
  }, [user]);

  // Initialize auth
  useEffect(() => {
    let mounted = true;
    
    const init = async () => {
      console.log("[Auth] ===== Initializing =====");
      console.log("[Auth] Stored redirect target:", getRedirectTarget() || "none");
      console.log("[Auth] Current URL:", window.location.href);
      
      // Check for recent login attempt (within 5 minutes)
      const loginTime = localStorage.getItem('transplacement_login_time');
      const isRecentLogin = loginTime && (Date.now() - parseInt(loginTime)) < 5 * 60 * 1000;
      
      if (isRecentLogin) {
        console.log("[Auth] Recent login detected, waiting longer for redirect result...");
        // Clear the login time marker to prevent future loops
        localStorage.removeItem('transplacement_login_time');
      }
      
      // Always clear loginInProgress on page load
      loginInProgress.current = false;
      
      // Step 1: Check for existing valid token first
      const existingUser = await verifyToken();
      if (existingUser && mounted) {
        console.log("[Auth] Restored from existing token");
        handleLoginSuccess(existingUser);
        return;
      }
      
      // Step 2: Check for Firebase redirect result (crucial for iOS Safari)
      if (!hasCheckedRedirect.current) {
        hasCheckedRedirect.current = true;
        console.log("[Auth] Checking redirect result...");
        
        try {
          // Give Firebase more time to initialize on mobile, especially for recent logins
          const waitTime = isRecentLogin ? 500 : 100;
          await new Promise(r => setTimeout(r, waitTime));
          
          const result = await getRedirectResult(auth);
          if (result?.user && mounted) {
            console.log("[Auth] ✓ Got redirect user:", result.user.email);
            const userData = await createSession(result.user);
            if (userData && mounted) {
              handleLoginSuccess(userData);
              return;
            }
          } else {
            console.log("[Auth] No redirect result");
          }
        } catch (err) {
          console.log("[Auth] Redirect check error:", err.code, err.message);
          
          // Handle "missing initial state" error gracefully
          if (err.message?.includes('missing initial state')) {
            console.log("[Auth] Missing initial state - storage may have been cleared");
            // Clear any stale redirect target
            localStorage.removeItem(REDIRECT_KEY);
            // Don't show error - just proceed to show login button
          } else if (err.code === 'auth/popup-closed-by-user') {
            // Normal - user cancelled
            console.log("[Auth] User cancelled login");
          } else if (err.code === 'auth/network-request-failed') {
            toast.error("Network error. Please check your connection.");
          }
          // For other errors, just log and continue
        }
      }
      
      // Step 3: Wait for Firebase to potentially restore auth state
      // This is important for iOS Safari where auth state may take longer to sync
      // Wait longer if this is following a recent login attempt
      const stateWaitTime = isRecentLogin ? 2000 : 1000;
      console.log("[Auth] Waiting for Firebase auth state...", stateWaitTime, "ms");
      await new Promise(r => setTimeout(r, stateWaitTime));
      
      // Step 4: Check if Firebase has a current user now
      if (auth.currentUser && mounted) {
        console.log("[Auth] Found Firebase currentUser:", auth.currentUser.email);
        const userData = await createSession(auth.currentUser);
        if (userData && mounted) {
          handleLoginSuccess(userData);
          return;
        }
      }
      
      // No auth found after all checks
      console.log("[Auth] No authentication found");
      if (mounted) {
        setRedirectTarget(null); // Clear any stale redirect target
        setLoading(false);
      }
    };

    init();
    
    // Listen for auth state changes - this is the final source of truth
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!mounted) return;
      
      // Prevent duplicate processing of the same user
      const userEmail = firebaseUser?.email || null;
      if (lastAuthUser.current === userEmail) {
        console.log("[Auth] Same user, skipping duplicate auth state change");
        return;
      }
      lastAuthUser.current = userEmail;
      
      console.log("[Auth] Auth state changed:", firebaseUser?.email || "signed out");
      
      // Clear login in progress flag
      loginInProgress.current = false;
      
      if (firebaseUser) {
        // Check if we already have a valid session for this user
        if (user && user.email === firebaseUser.email) {
          console.log("[Auth] Already have session for this user");
          setLoading(false);
          return;
        }
        
        // Check if we have a valid token
        if (getToken()) {
          const userData = await verifyToken();
          if (userData) {
            handleLoginSuccess(userData);
            return;
          }
        }
        
        // Create new session from Firebase user
        const userData = await createSession(firebaseUser);
        if (userData && mounted) {
          handleLoginSuccess(userData);
        } else {
          setLoading(false);
        }
      } else {
        // Signed out - clear everything
        setToken(null);
        setUser(null);
        setLoading(false);
        localStorage.removeItem(REDIRECT_KEY);
        localStorage.removeItem('transplacement_login_time');
      }
    });
    
    // Safety timeout - increased for iOS Safari which may take longer
    const timeout = setTimeout(() => {
      if (mounted && loading) {
        console.log("[Auth] Safety timeout reached - completing init");
        setLoading(false);
      }
    }, 15000); // 15 seconds for iOS Safari
    
    return () => {
      mounted = false;
      unsubscribe();
      clearTimeout(timeout);
    };
  }, [verifyToken, createSession, handleLoginSuccess]);

  // Check if iOS Safari (needs special handling due to storage partitioning)
  const isIOSSafari = useCallback(() => {
    const ua = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua);
    const webkit = /WebKit/.test(ua);
    const notChrome = !/CriOS/.test(ua); // Not Chrome on iOS
    return iOS && webkit && notChrome;
  }, []);

  // Check if mobile device
  const isMobile = useCallback(() => {
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }, []);

  // Login function - works on ALL devices (iOS, Android, Desktop)
  const login = useCallback(async (redirectTo = '/submit') => {
    // Prevent duplicate login attempts (crucial for iOS)
    if (loginInProgress.current) {
      console.log("[Auth] Login already in progress, skipping");
      return;
    }
    
    // If user is already logged in, just redirect
    if (user) {
      console.log("[Auth] User already logged in, redirecting to:", redirectTo);
      setPendingRedirect(redirectTo);
      return;
    }
    
    // If session is still being created, wait
    if (sessionLoading || isCreatingSession.current) {
      console.log("[Auth] Session creation in progress, skipping login");
      return;
    }
    
    console.log("[Auth] === Login started ===");
    console.log("[Auth] Redirect target:", redirectTo);
    console.log("[Auth] Is iOS Safari:", isIOSSafari());
    console.log("[Auth] Is mobile:", isMobile());
    
    // Mark login as in progress
    loginInProgress.current = true;
    
    // Save redirect target to localStorage (survives page redirects)
    localStorage.setItem(REDIRECT_KEY, redirectTo);
    
    // Also save a timestamp to detect stale login attempts
    localStorage.setItem('transplacement_login_time', Date.now().toString());
    
    // Reset session creation state for new login attempt
    // DO NOT reset hasCheckedRedirect - this prevents loops
    isCreatingSession.current = false;
    
    // Use redirect for ALL devices - most reliable across browsers
    // Popup has COOP issues on some hosting environments
    console.log("[Auth] Using redirect flow for all devices");
    try {
      await signInWithRedirect(auth, googleProvider);
      // Page will redirect to Google, then back to our app
    } catch (err) {
      console.error("[Auth] Redirect error:", err.code, err.message);
      localStorage.removeItem(REDIRECT_KEY);
      localStorage.removeItem('transplacement_login_time');
      loginInProgress.current = false;
      localStorage.removeItem('transplacement_login_time');
      loginInProgress.current = false;
      
      if (err.code === 'auth/network-request-failed') {
        toast.error("Network error. Please check your connection.");
      } else if (err.code === 'auth/unauthorized-domain') {
        toast.error("This domain is not authorized for sign-in.");
      } else {
        toast.error("Login failed. Please try again.");
      }
    }
  }, [isIOSSafari, isMobile, user, sessionLoading, setPendingRedirect]);

  // Logout function
  const logout = useCallback(async () => {
    console.log("[Auth] Logging out");
    
    try {
      await signOut(auth);
    } catch (err) {
      console.log("[Auth] Firebase signOut error:", err);
    }
    
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      console.log("[Auth] Backend logout error:", err);
    }
    
    setToken(null);
    setRedirectTarget(null);
    setUser(null);
    isCreatingSession.current = false;
    toast.success("Logged out");
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      sessionLoading,
      login, 
      logout, 
      setUser,
      pendingRedirect,
      setPendingRedirect 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// Redirect Handler Component
function RedirectHandler() {
  const { pendingRedirect, setPendingRedirect } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (pendingRedirect) {
      console.log("[Redirect] Navigating to:", pendingRedirect);
      navigate(pendingRedirect, { replace: true });
      setPendingRedirect(null);
    }
  }, [pendingRedirect, navigate, setPendingRedirect]);
  
  return null;
}

// Loading Spinner
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <motion.div
        className="flex flex-col items-center gap-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </motion.div>
    </div>
  );
}

// Page transition wrapper
function PageTransition({ children }) {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.2 }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

// Session Loading Overlay - shown during firebase-session API call
function SessionLoadingOverlay() {
  const { sessionLoading } = useAuth();
  
  if (!sessionLoading) return null;
  
  return (
    <motion.div
      className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="flex flex-col items-center gap-4 text-center px-4">
        <div className="w-12 h-12 border-3 border-primary border-t-transparent rounded-full animate-spin" />
        <div>
          <p className="text-lg font-medium">Signing you in...</p>
          <p className="text-sm text-muted-foreground mt-1">Please wait while we set up your session</p>
        </div>
      </div>
    </motion.div>
  );
}

// Main App Component
function AppContent() {
  const { loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <>
      <SessionLoadingOverlay />
      <RedirectHandler />
      <PageTransition>
        <div className="pb-20 md:pb-0">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/college/:collegeId" element={<CollegeDetail />} />
          <Route path="/submit" element={<SubmitPlacement />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/about" element={<AboutUs />} />
        </Routes>
        </div>
      </PageTransition>
      <BottomNav />
      <BackToTop />
      <Toaster position="top-center" richColors closeButton />
    </>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="transplacement-theme">
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
export { API, useAuth };
